// AdCopy AI Experimentation — Ad Copy section, deterministic check.
// Surfaces whether the (adopted) client has run Custom Experiments testing AdCopy AI per
// Tinuiti's I&G Search testing best practices. Cites the recommended test structure and
// early results so the auditor has the framing handy for client conversations.

import { titleize } from 'inflected';

import type { EvidenceProps } from './registry';

interface ExperimentSummary {
  experimentId?: string;
  name?: string;
  description?: string;
  status?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

interface ExperimentationShape {
  adopted: boolean;
  company: string;
  totalExperiments: number;
  adCopyAiExperimentCount: number;
  adCopyAiExperiments: ExperimentSummary[];
  recommendation: {
    framework: string;
    controlArm: string;
    experimentArm: string;
    analysisGuidance: string[];
    earlyResults: { client: string; metric: string }[];
  };
}

function formatStatus(s?: string): string {
  if (!s) return '—';
  return titleize(s);
}

export const AdCopyAiExperimentationEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as ExperimentationShape;
  const hasTests = e.adCopyAiExperimentCount > 0;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        {hasTests ? (
          <>
            <strong>{e.company}</strong> has <strong>{e.adCopyAiExperimentCount}</strong> Custom
            Experiment{e.adCopyAiExperimentCount === 1 ? '' : 's'} on record that match the AdCopy
            AI signal — validating impact in line with Tinuiti&apos;s testing best practices.
          </>
        ) : (
          <>
            <strong>{e.company}</strong> is using AdCopy AI but has no Custom Experiments matching
            the AdCopy AI signal across {e.totalExperiments} total experiment
            {e.totalExperiments === 1 ? '' : 's'} on the account. Per Tinuiti&apos;s I&amp;G Search
            guidance, all AdCopy AI rollouts should be validated via Custom Experiments — and early
            results across the agency portfolio have been substantial.
          </>
        )}
      </p>

      {hasTests && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Matching experiments
            <span className='evidence__section-tag evidence__section-tag--ok'>
              {e.adCopyAiExperimentCount} found
            </span>
          </h4>
          <table className='evidence__table evidence__table--experiments'>
            <thead>
              <tr>
                <th>Experiment</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              {e.adCopyAiExperiments.map(x => (
                <tr key={x.experimentId ?? x.name}>
                  <td>
                    <div>{x.name ?? '(unnamed)'}</div>
                    {x.description && <div className='evidence__action-id'>{x.description}</div>}
                  </td>
                  <td>{formatStatus(x.status)}</td>
                  <td>{x.startDate ?? '—'}</td>
                  <td>{x.endDate ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Recommended test structure
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.recommendation.framework}
          </span>
        </h4>
        <div className='evidence__arms'>
          <div className='evidence__arm'>
            <div className='evidence__arm-label'>Control arm</div>
            <div className='evidence__arm-body'>{e.recommendation.controlArm}</div>
          </div>
          <div className='evidence__arm evidence__arm--treatment'>
            <div className='evidence__arm-label'>Experiment arm</div>
            <div className='evidence__arm-body'>{e.recommendation.experimentArm}</div>
          </div>
        </div>
        <ul className='evidence__guidance'>
          {e.recommendation.analysisGuidance.map((g, i) => (
            <li key={i}>{g}</li>
          ))}
        </ul>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Early results across the portfolio</h4>
        <div className='evidence__early-results'>
          {e.recommendation.earlyResults.map(r => (
            <div key={r.client} className='evidence__early-result'>
              <div className='evidence__early-result-client'>{r.client}</div>
              <div className='evidence__early-result-metric'>{r.metric}</div>
            </div>
          ))}
        </div>
      </section>

      <p className='evidence__footnote'>
        Detection matches experiment names/descriptions against the AdCopy AI signal (any historical
        experiment, no status or time filter). Verifying that each experiment followed the
        recommended structure (paused evergreens, used AdCopy AI assets) is an audit follow-up
        rather than a detection step.
      </p>
    </div>
  );
};
